import React, { useState } from 'react';
import Modal from '../../ui/Modal';
import { Button } from '../../ui/PageShell';
import Table from '../../ui/Table';
import Tabs from '../../ui/Tabs';
import useData from '../useData';
import './users.css';

/**
 * The two login loggers, per identity.
 *
 *   الأجهزة   login_sessions — who actually got IN, and from what
 *             device. Answers "is there a phone signed in that this
 *             family doesn't recognise?"
 *   المحاولات login_attempts — everything TRIED, success or failure.
 *             Answers the two questions the office actually gets:
 *             "ليش ما وصلني الكود؟" and "في حدا عم يحاول يدخل عحسابي؟"
 *
 *   تنبيهات  security_events — things that should not happen: a login
 *             token used from a different browser/device than the one
 *             that logged in (the session is killed on the spot), or a
 *             burst of wrong codes (the account is paused 15 minutes).
 *
 * Both tables already existed and were already being written to — they
 * simply had no screen. This is that screen.
 */

const fmt = (iso) => (iso ? String(iso).slice(0, 16).replace('T', ' ') : '—');

const STEP_LABEL = {
  lookup: 'بحث عن الاسم',
  select: 'اختيار الحساب',
  verify: 'إدخال الرمز',
  access_code: 'رمز الدخول',
};

// Outcomes, in the words the office would use. Anything unmapped shows
// raw rather than being hidden — a new outcome must not vanish.
const EVENT_LABEL = {
  device_mismatch: 'استُعمل التوكن من جهاز آخر — أُلغيت الجلسة',
  too_many_wrong_codes: 'رموز خاطئة كثيرة — إيقاف مؤقت ١٥ دقيقة',
  too_many_lookups: 'بحث متكرر عن أسماء غير موجودة',
};

// expires_at is SQLite's "YYYY-MM-DD HH:MM:SS" in UTC.
const isExpired = (s) => !!s && new Date(`${String(s).replace(' ', 'T')}Z`) < new Date();

const OUTCOME = {
  sent: { label: 'أُرسل الرمز', tone: 'info' },
  cooldown_resent: { label: 'الرمز السابق ما زال صالحاً', tone: 'info' },
  access_code_prompt: { label: 'طُلب رمز الدخول', tone: 'info' },
  code_correct: { label: 'دخول ناجح', tone: 'success' },
  master_code: { label: 'دخول خاص', tone: 'warning' },
  code_wrong: { label: 'رمز خاطئ', tone: 'danger' },
  not_found: { label: 'حساب غير موجود', tone: 'warning' },
  ambiguous: { label: 'أكثر من حساب بنفس الاسم', tone: 'warning' },
  locked: { label: 'الحساب موقوف', tone: 'danger' },
  rate_limited: { label: 'تجاوز عدد المحاولات', tone: 'danger' },
  error: { label: 'خطأ', tone: 'danger' },
};

export default function UserLoginHistoryModal({ user, onClose, api }) {
  const [tab, setTab] = useState('sessions');
  const { data, loading, error } = useData(() => api.loginHistory(user.id), [user.id]);

  const sessions = (data?.sessions || []).map((s) => ({
    ...s,
    // Sessions slide: each use pushes expiry 30 days ahead, so an
    // unrevoked session past its expiry was simply not used for a month.
    state: s.revoked_at || isExpired(s.expires_at) ? 'منتهية' : 'نشطة',
    ip: s.last_ip || s.ip_address || '—',
  }));
  const events = (data?.events || []).map((e) => ({
    ...e,
    kind_label: EVENT_LABEL[e.kind] || e.kind,
  }));
  const attempts = (data?.attempts || []).map((a) => ({
    ...a,
    step_label: STEP_LABEL[a.step] || a.step,
    outcome_label: OUTCOME[a.outcome]?.label || a.outcome,
  }));

  const activeCount = sessions.filter((s) => s.state === 'نشطة').length;

  return (
    <Modal
      open
      size="lg"
      title={`سجل الدخول — ${user.name}`}
      onClose={onClose}
      footer={<Button variant="secondary" onClick={onClose}>إغلاق</Button>}
    >
      {error && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>}

      <Tabs
        active={tab}
        onChange={setTab}
        items={[
          { id: 'sessions', label: 'الأجهزة', badge: activeCount || null },
          { id: 'attempts', label: 'المحاولات', badge: attempts.length || null },
          { id: 'events', label: 'تنبيهات أمنية', badge: events.length || null },
        ]}
      />

      {tab === 'events' ? (
        <Table
          keyField="id"
          rows={events}
          loading={loading}
          emptyText="لا توجد تنبيهات — لم يُرصد أي استعمال مشبوه"
          searchableKeys={['kind_label', 'ip_address', 'user_agent']}
          pageSize={10}
          exportName={`security-events-${user.id}`}
          columns={[
            { key: 'created_at', label: 'الوقت', nowrap: true, formatValue: fmt },
            { key: 'kind_label', label: 'الحدث', width: 2, badge: () => 'danger' },
            { key: 'ip_address', label: 'IP', nowrap: true },
            { key: 'user_agent', label: 'الجهاز/المتصفح', width: 2, clamp: 1 },
          ]}
        />
      ) : tab === 'sessions' ? (
        <Table
          keyField="id"
          rows={sessions}
          loading={loading}
          emptyText="لم يسجّل الدخول من أي جهاز بعد"
          searchPlaceholder="ابحث في الأجهزة..."
          searchableKeys={['device_label']}
          pageSize={10}
          columns={[
            { key: 'device_label', label: 'الجهاز', width: 2, clamp: 1 },
            { key: 'last_login_at', label: 'أول دخول', nowrap: true, formatValue: fmt },
            { key: 'last_seen_at', label: 'آخر نشاط', nowrap: true, formatValue: fmt },
            { key: 'ip', label: 'IP', nowrap: true },
            {
              key: 'state',
              label: 'الحالة',
              nowrap: true,
              align: 'center',
              badge: (v) => (v === 'نشطة' ? 'success' : 'neutral'),
            },
          ]}
        />
      ) : (
        <Table
          keyField="id"
          rows={attempts}
          loading={loading}
          emptyText="لا توجد محاولات مسجّلة"
          searchPlaceholder="ابحث في المحاولات..."
          searchableKeys={['step_label', 'outcome_label', 'ip_address']}
          pageSize={10}
          exportName={`login-attempts-${user.id}`}
          columns={[
            { key: 'created_at', label: 'الوقت', nowrap: true, formatValue: fmt },
            { key: 'step_label', label: 'الخطوة', nowrap: true },
            {
              key: 'outcome_label',
              label: 'النتيجة',
              nowrap: true,
              // Tone is read off the RAW outcome on the row, not the
              // translated label, so renaming a label never silently
              // changes a colour.
              badge: (v, row) => OUTCOME[row?.outcome]?.tone || 'neutral',
            },
            { key: 'ip_address', label: 'IP', nowrap: true },
            { key: 'user_agent', label: 'الجهاز/المتصفح', width: 2, clamp: 1 },
          ]}
        />
      )}
    </Modal>
  );
}
